import React, { useEffect, useMemo, useState } from 'react'
import Container from '../../elements/Container'
import MeasurementHeader from './_components/measurementHeader'
import { MeasurementTabTypes } from '../../_config/types'
import Data from './Data'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useExperimentApi } from '../../store/api/hooks/experimentApiHook'
import toast from 'react-hot-toast'
import { URLS } from '../../_config'
import { useExperimentSelector } from '../../store/selectors/experimentSelector'
import Icon from '../../components/icons'
import { trackEvent } from '../../_common/functions'
import MTEMeasurementConfigurePage from './MeasurementConfigure/MTE'
import DMLMeasurementConfigurePage from './MeasurementConfigure/DML'
import ProcessData from './Data/_components/processData/ProcessData'
import RCTMultiFileConfigurePage from './MeasurementConfigure/RCT/rctMultiFileConfigure'
import MeasurementConfigure from './MeasurementConfigure'
import { EXPERIMENT_TYPES } from '../../_common/constant'

interface MeasurementFlowProps {
    popup?: boolean
}

const MeasurementFlow: React.FC<MeasurementFlowProps> = ({ popup = false }) => {
    const experimentAPI: any = useExperimentApi()
    const params: any = useParams()
    const navigate = useNavigate()
    const experiment = useExperimentSelector()
    const [searchParams] = useSearchParams()
    const [conversionFlow, setConversionFlow] = useState<boolean>(false)
    const [tab, setTab] = useState<MeasurementTabTypes>('data')
    const [dataType, setDataType] = useState<'Measurement' | 'Intervention' | 'Recommendation'>('Measurement')
    const [filterKey, setFilterKey] = useState<'measurement_table_filters' | 'intervention_table_filters' | 'recommendation_table_filters'>('measurement_table_filters')
    const [forParam, setForParam] = useState<string | null>(null)
    const measurementType = experiment?.experiment_type?.title == 'Observational Study' ? 'OSTUDY' : 'RCT'

    const safeNavigate = (to: string) => {
        const result: any = navigate(to)
        if (result && typeof result.catch === 'function') {
            void result.catch(() => undefined)
        }
    }

    useEffect(() => {
        if (!popup) {
            setForParam(searchParams.get('for'))
        }
    }, [searchParams])

    useEffect(() => {
        if (forParam) {
            setFilterKey(forParam === 'intervention' ? 'intervention_table_filters' : 'recommendation_table_filters')
            setDataType(forParam === 'intervention' ? 'Intervention' : 'Recommendation')
        } else {
            setFilterKey('measurement_table_filters')
            setDataType('Measurement')
        }
    }, [forParam])

    const getExperimentDetails = () => {
        experimentAPI.getExperimentDetails(
            { literal: params.experiment + '?use_orm=true' },
            (resp: any) => {
                if (!resp.data.experiment_metadatas?.metrices) {
                    setTimeout(getExperimentDetails, 1000)
                }
            },
            (error: any) => {
                toast.error(error.message)
                safeNavigate(URLS.MY_EXPERIMENTS)
            },
        )
    }

    useEffect(() => {
        if (params.experiment) {
            getExperimentDetails()
        }
        if (params.step) {
            setTab(params.step)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params])

    useEffect(() => {
        if (experiment) {
            trackEvent('measurement_flow_opened', { experiment_literal: experiment?.experiment_literal, experiment_type: experiment?.experiment_type?.title })
        }
    }, [experiment])

    useEffect(() => {
        if (experiment?.experiment_metadatas?.metrices != null) {
            let flow: boolean = false
            experiment.experiment_metadatas?.metrices.map((kpi: any) => {
                if (kpi.literral_value == 'Incremental Conversion') {
                    flow = true
                }
            })
            setConversionFlow(flow)
        }
    }, [experiment])

    const configurePrev = () => {
        if (conversionFlow) {
            if (popup) {
                setForParam('recommendation')
                return
            }
            safeNavigate(URLS.MEASUREMENT(params.experiment, 'filter', 'for=recommendation'))
            return
        }

        if (popup) {
            setTab('filter')
            return
        }

        safeNavigate(URLS.MEASUREMENT(params.experiment, 'filter'))
    }

    const getTabContent = useMemo(() => {
        switch (tab) {
            case 'data':
                return (
                    <Data
                        popup={popup}
                        next={() => {
                            if (popup) {
                                setTab('filter')
                                return
                            }

                            safeNavigate(URLS.MEASUREMENT(params.experiment, 'filter'))
                        }}
                    />
                )
            case 'filter':
                return (
                    <Container size="md">
                        <div data-testid="tab-filter" className="shadow-1 rounded-[0.833rem] p-5">
                            <ProcessData
                                data-testid="filter-tab"
                                dataType={dataType}
                                filterKey={filterKey}
                                conversionFlow={conversionFlow}
                                next={() => {
                                    let multiFileFlow: 'intervention' | 'recommendation' | null = null
                                    const lastFile = Boolean(popup ? forParam : searchParams.get('for'))
                                    if (experiment?.experiment_type?.title === EXPERIMENT_TYPES.MTE.title) {
                                        multiFileFlow = 'intervention'
                                    }
                                    if (conversionFlow) {
                                        multiFileFlow = 'recommendation'
                                    }

                                    if (multiFileFlow && !lastFile) {
                                        if (popup) {
                                            setForParam(multiFileFlow)
                                            return
                                        }
                                        safeNavigate(URLS.MEASUREMENT(params.experiment, 'filter', 'for=' + multiFileFlow))
                                        return
                                    }

                                    if (popup) {
                                        setTab('configure')
                                        return
                                    }

                                    safeNavigate(URLS.MEASUREMENT(params.experiment, 'configure'))
                                }}
                                prev={() => {
                                    const multiFilter = popup ? forParam : searchParams.get('for')
                                    if (multiFilter) {
                                        if (popup) {
                                            setForParam(null)
                                            return
                                        }
                                        safeNavigate(URLS.MEASUREMENT(params.experiment, 'filter'))
                                        return
                                    }

                                    if (popup) {
                                        setTab('data')
                                        return
                                    }

                                    safeNavigate(URLS.MEASUREMENT(params.experiment, 'data'))
                                }}
                                _for={experiment?.experiment_type?.title}
                            />
                        </div>
                    </Container>
                )
            case 'configure': {
                if (conversionFlow) {
                    return <RCTMultiFileConfigurePage prev={configurePrev} />
                }

                if (experiment?.experiment_type?.title == EXPERIMENT_TYPES.MTE.title) {
                    return <MTEMeasurementConfigurePage />
                }

                if (experiment?.experiment_type?.title == EXPERIMENT_TYPES.DML.title) {
                    return <DMLMeasurementConfigurePage />
                }

                return <MeasurementConfigure prev={configurePrev} type={measurementType} />
            }

            default:
                return (
                    <div>
                        <Icon name="loader" />
                    </div>
                )
        }
    }, [tab, experiment, dataType, filterKey, forParam, searchParams])

    return (
        <>
            {!popup && <MeasurementHeader tab={tab} />}
            {getTabContent}
        </>
    )
}

export default MeasurementFlow
